import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { mockAuthHeaders } from '../utils/mockAuth';

// ============================================================================
// COMPANY PAGE — Phase 1
//
// Persistent header + Fit Strip + tabbed content (Hiring Trends · Explore Roles
// · Company Values · News (soon) · Employee Benefits).
//
// Phase 1 wires up: header + firmographics, fit strip aggregations from
// jobs[], the existing jobs table (Explore Roles), and tab deep-linking.
// All other tabs and several header fields are clearly placeholder'd —
// see // PHASE N — comments throughout for where the work picks up.
//
// The mockup in instructions/Company_Page_Final.jsx is the destination.
// We've converted Tailwind to inline styles and dropped the stub Sidebar
// (the app shell already provides it).
// ============================================================================

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ---- Palette (from the mockup; matches landing page palette exactly) -------
const SAGE = {
  50: "#f6f7f4", 100: "#eaede5", 200: "#d5dbcc", 300: "#b5c0a5",
  400: "#8fa17a", 500: "#6b8455", 600: "#546a42", 700: "#3f4f32",
  800: "#2d3824", 900: "#1c2317",
};
const CREAM = {
  50: "#fefdfb", 100: "#fdf9f1", 200: "#faf3e3", 300: "#f5ead0", 400: "#ecdcb3",
};
const WARM = { accent: "#c4956a", accentLight: "#e8d5c0", rose: "#c48a8a", roseLight: "#f0e0e0" };
const F = "'Outfit', sans-serif";
const SERIF = "'Lora', Georgia, serif";

const TONE = {
  good: { color: "#3d7a4a", bg: "#e4f2e7", border: "#3d7a4a40" },
  neutral: { color: SAGE[700], bg: SAGE[100], border: SAGE[300] },
  stretch: { color: WARM.accent, bg: WARM.accentLight, border: WARM.accent + "40" },
  prompt: { color: SAGE[500], bg: CREAM[200], border: SAGE[200] },
};

// ----------------------------------------------------------------------------
// Tabs
// ----------------------------------------------------------------------------
const TABS = [
  { id: "hiring", label: "Hiring Trends" },
  { id: "explore", label: "Explore Roles" },
  { id: "values", label: "Company Values" },
  { id: "news", label: "News", soon: true },
  { id: "benefits", label: "Employee Benefits" },
];

// ----------------------------------------------------------------------------
// Aggregations driven by real backend data
// ----------------------------------------------------------------------------

// Deterministic color for the company logo bubble — Phase 4 will replace
// this with a scraped logo URL when we have one.
function logoColorForDomain(domain) {
  if (!domain) return SAGE[600];
  let h = 0;
  for (let i = 0; i < domain.length; i++) h = (h * 31 + domain.charCodeAt(i)) >>> 0;
  const palette = [
    "#635bff", // stripe-ish indigo
    SAGE[600], SAGE[500], WARM.accent, "#6366f1",
    "#0ea5e9", "#0891b2", "#7c3aed", "#db2777", "#f59e0b",
  ];
  return palette[h % palette.length];
}

// Take the company's filtered jobs[] and produce the four fit-strip slices.
function computeFitSlices(jobs) {
  const total = jobs.length || 1;

  // Roles — bucket by job_family
  const familyCounts = {};
  jobs.forEach((j) => {
    const f = j.job_family || "Other";
    familyCounts[f] = (familyCounts[f] || 0) + 1;
  });
  const familyColors = [SAGE[600], SAGE[500], WARM.accent, CREAM[400], SAGE[400], SAGE[300]];
  const rolesSorted = Object.entries(familyCounts).sort((a, b) => b[1] - a[1]);
  const ROLES_TOP = rolesSorted.slice(0, 5).map(([name, n], i) => ({
    name, pct: Math.round((n / total) * 100), color: familyColors[i] || SAGE[300],
  }));
  const remainderRoles = rolesSorted.slice(5).reduce((s, [, n]) => s + n, 0);
  const ROLES = remainderRoles > 0
    ? [...ROLES_TOP, { name: "Other", pct: Math.round((remainderRoles / total) * 100), color: SAGE[300] }]
    : ROLES_TOP;

  // Locations — group by markets[] (seattle_msa, us_remote, etc.)
  // Backend currently filters to seattle_msa + us_remote, so we'll mostly see those.
  const MARKET_LABELS = {
    seattle_msa: "Seattle",
    us_remote: "Remote (US)",
    sf_bay: "SF Bay",
    nyc: "New York",
    la: "Los Angeles",
    boston: "Boston",
    chicago: "Chicago",
    dc: "DC",
    austin: "Austin",
    denver: "Denver",
    atlanta: "Atlanta",
    portland: "Portland",
    global_remote: "Global Remote",
    international: "International",
  };
  // Each job is counted once against its primary market, so percentages
  // sum to 100%. When a posting carries multiple markets (common: a Seattle
  // hybrid role often also lists us_remote) we prefer the named metro over
  // remote, since "where the office is" is the more useful answer for fit.
  const METRO_PRIORITY = [
    "seattle_msa", "sf_bay", "nyc", "la", "boston", "chicago",
    "dc", "austin", "denver", "atlanta", "portland", "international",
    "us_remote", "global_remote",
  ];
  const locCounts = {};
  jobs.forEach((j) => {
    const markets = j.markets || [];
    const primary = METRO_PRIORITY.find((m) => markets.includes(m)) || markets[0];
    if (primary) locCounts[primary] = (locCounts[primary] || 0) + 1;
  });
  const locColors = [SAGE[600], SAGE[500], WARM.accent, CREAM[400], SAGE[400]];
  const locSorted = Object.entries(locCounts).sort((a, b) => b[1] - a[1]);
  const locTotal = jobs.length || 1;
  const LOCATIONS_TOP = locSorted.slice(0, 5).map(([m, n], i) => ({
    name: MARKET_LABELS[m] || m,
    pct: Math.round((n / locTotal) * 100),
    color: locColors[i] || SAGE[300],
  }));
  const remainderLoc = locSorted.slice(5).reduce((s, [, n]) => s + n, 0);
  const LOCATIONS = remainderLoc > 0
    ? [...LOCATIONS_TOP, { name: "Other", pct: Math.round((remainderLoc / locTotal) * 100), color: SAGE[300] }]
    : LOCATIONS_TOP;

  // Seniority — bucket by job.level.
  // Title-extractor levels are strings like "Senior", "Staff", "Principal";
  // group them into the mockup's bands.
  const senCounts = { jr: 0, mid: 0, sr: 0, staff: 0, prin: 0, other: 0 };
  jobs.forEach((j) => {
    const lvl = (j.level || "").toLowerCase();
    if (!lvl) senCounts.other += 1;
    else if (/(intern|junior|associate|entry|i\b|level 1|level 2|l1|l2|l3)/.test(lvl)) senCounts.jr += 1;
    else if (/(mid|ii\b|level 4|l4)/.test(lvl)) senCounts.mid += 1;
    else if (/(senior|sr\.?|iii\b|level 5|l5)/.test(lvl)) senCounts.sr += 1;
    else if (/(staff|iv\b|level 6|l6)/.test(lvl)) senCounts.staff += 1;
    else if (/(principal|distinguished|fellow|director|vp|head|chief|level 7|l7)/.test(lvl)) senCounts.prin += 1;
    else senCounts.other += 1;
  });
  const SENIORITY_BANDS = [
    { id: "jr", name: "Junior", color: SAGE[300] },
    { id: "mid", name: "Mid", color: SAGE[400] },
    { id: "sr", name: "Senior", color: SAGE[500] },
    { id: "staff", name: "Staff", color: SAGE[600] },
    { id: "prin", name: "Principal+", color: SAGE[700] },
    { id: "other", name: "Other", color: SAGE[200] },
  ];
  const SENIORITY = SENIORITY_BANDS
    .filter((b) => senCounts[b.id] > 0)
    .map((b) => ({ name: b.name, pct: Math.round((senCounts[b.id] / total) * 100), color: b.color }));
  // Senior–Staff combined headline stat for the seniority tile
  const seniorStaffPct = Math.round(((senCounts.sr + senCounts.staff) / total) * 100);

  return { ROLES, LOCATIONS, SENIORITY, seniorStaffPct };
}

// ----------------------------------------------------------------------------
// Small shared building blocks
// ----------------------------------------------------------------------------

function StackedBar({ items, height = 6 }) {
  return (
    <div style={{ display: "flex", width: "100%", borderRadius: 9999, overflow: "hidden", height }}>
      {items.map((item) => (
        <div key={item.name} style={{ width: `${item.pct}%`, background: item.color }} title={`${item.name}: ${item.pct}%`} />
      ))}
    </div>
  );
}

function TabBar({ activeTab, onChange }) {
  return (
    <div
      className="fade-up"
      style={{
        borderBottom: `1px solid ${SAGE[200]}`,
        display: "flex",
        alignItems: "center",
        gap: 4,
        paddingLeft: 4,
        paddingRight: 4,
        marginTop: 20,
        overflowX: "auto",
      }}
    >
      {TABS.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => !tab.soon && onChange(tab.id)}
            disabled={tab.soon}
            style={{
              position: "relative",
              padding: "12px 16px",
              fontSize: 14,
              fontWeight: 500,
              color: isActive ? SAGE[800] : tab.soon ? SAGE[300] : SAGE[500],
              fontFamily: F,
              cursor: tab.soon ? "default" : "pointer",
              background: "transparent",
              border: "none",
              display: "flex",
              alignItems: "center",
              gap: 8,
              whiteSpace: "nowrap",
            }}
          >
            {tab.label}
            {tab.soon && (
              <span
                style={{
                  fontSize: 9,
                  padding: "2px 6px",
                  borderRadius: 9999,
                  fontWeight: 600,
                  background: CREAM[300],
                  color: SAGE[500],
                  fontFamily: F,
                }}
              >
                SOON
              </span>
            )}
            {isActive && (
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 12,
                  right: 12,
                  height: 2,
                  borderTopLeftRadius: 9999,
                  borderTopRightRadius: 9999,
                  background: SAGE[600],
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Company Header
// ----------------------------------------------------------------------------
function CompanyHeader({ company, totalOpenRoles }) {
  const initial = (company.name || company.domain || "?").trim().charAt(0).toUpperCase();
  const logoColor = logoColorForDomain(company.domain);
  const meta = company.metadata || {};

  // PHASE 5 — Watch button + watcher count are visual only.
  // Hook up to a /api/watches endpoint when persona/preferences pipeline lands.
  const onWatchClick = () => {
    // no-op; visual only for Phase 1
  };

  return (
    <div
      className="fade-up"
      style={{
        borderRadius: 16,
        padding: 24,
        background: CREAM[50],
        border: `2px solid ${SAGE[200]}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 20 }}>
        {/* Logo — real image when profile.json supplies one (Clearbit), else
            an initial-letter bubble with a deterministic background color. */}
        {company.logo_url ? (
          <img
            src={company.logo_url}
            alt={`${company.name} logo`}
            style={{
              flexShrink: 0,
              width: 64,
              height: 64,
              borderRadius: 16,
              objectFit: "contain",
              background: CREAM[100],
              border: `1px solid ${SAGE[100]}`,
            }}
          />
        ) : (
          <div
            style={{
              flexShrink: 0,
              width: 64,
              height: 64,
              borderRadius: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              fontWeight: 700,
              background: logoColor,
              color: "#fff",
              fontFamily: F,
            }}
          >
            {initial}
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <h1 style={{ fontSize: 30, fontWeight: 600, color: SAGE[800], fontFamily: SERIF, margin: 0 }}>
              {company.name}
            </h1>
            {company.tagline && (
              <span style={{ fontSize: 13, color: SAGE[500], fontFamily: F }}>
                {company.tagline}
              </span>
            )}
          </div>

          {/* Firmographics — only fields we have today. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              columnGap: 16,
              rowGap: 4,
              marginTop: 8,
              flexWrap: "wrap",
              fontSize: 12,
              color: SAGE[500],
              fontFamily: F,
            }}
          >
            {meta.sector && <span>{meta.sector}</span>}
            {meta.sector && meta.size_tier && <span style={{ color: SAGE[300] }}>·</span>}
            {meta.size_tier && <span>{meta.size_tier}</span>}
            {(meta.sector || meta.size_tier) && meta.hq_location && (
              <span style={{ color: SAGE[300] }}>·</span>
            )}
            {meta.hq_location && <span>HQ {meta.hq_location}</span>}
            {/* PHASE 4 — Founded year + funding to be added once firmographics scrape grows. */}
          </div>

          {/* Synopsis — populated from profile.json (scraped marketing copy
              via scripts/enrich_company.py); falls back to a placeholder. */}
          {company.synopsis ? (
            <p
              style={{
                marginTop: 14,
                marginBottom: 0,
                fontSize: 13,
                lineHeight: 1.55,
                color: SAGE[600],
                fontFamily: F,
                maxWidth: 720,
              }}
            >
              {company.synopsis}
            </p>
          ) : (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                border: `1px dashed ${SAGE[300]}`,
                borderRadius: 10,
                background: CREAM[100],
                maxWidth: 720,
                color: SAGE[500],
                fontFamily: F,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              Synopsis coming soon — run <code>scripts/enrich_company.py {company.domain}</code> to populate.
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 8,
            flexShrink: 0,
          }}
        >
          {/* PHASE 5 — Watch button (visual only). */}
          <button
            onClick={onWatchClick}
            title="Coming soon"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600,
              background: "transparent",
              color: SAGE[700],
              border: `1.5px solid ${SAGE[300]}`,
              fontFamily: F,
              cursor: "default",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
            </svg>
            Watch
          </button>
          {/* PHASE 5 — watcher count goes here once the Watch backend ships. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 10px",
              borderRadius: 8,
              background: SAGE[100],
            }}
          >
            <span style={{ fontSize: 10, fontWeight: 600, color: SAGE[700], fontFamily: F }}>
              {totalOpenRoles} open roles
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Fit Strip
// ----------------------------------------------------------------------------
function FitStrip({ slices, totalOpenRoles }) {
  // PHASE 1 — Always show empty-state insights (cream/sage prompt tiles).
  // Phase ?: profile-aware insights live in a separate pipeline.
  const insights = {
    role: { tone: "prompt", headline: "Add target roles", detail: "Tell us what you're looking for" },
    location: { tone: "prompt", headline: "Set your markets", detail: "Add preferred locations" },
    seniority: { tone: "prompt", headline: "Add experience", detail: "We'll show your level fit" },
    comp: { tone: "prompt", headline: "Set target comp", detail: "We'll compare against bands" },
  };

  const Tile = ({ label, headlineStat, headlineLabel, items, insight, isComp }) => {
    const t = TONE[insight.tone];
    return (
      <div
        style={{
          borderRadius: 12,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          background: CREAM[50],
          border: `1.5px solid ${SAGE[200]}`,
        }}
      >
        <div style={{ padding: "16px 20px", flex: 1 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 8,
              color: SAGE[400],
              fontFamily: F,
            }}
          >
            {label}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: SAGE[800], fontFamily: SERIF }}>
              {headlineStat}
            </span>
            <span style={{ fontSize: 12, color: SAGE[500], fontFamily: F }}>{headlineLabel}</span>
          </div>
          {!isComp ? (
            <>
              <StackedBar items={items} height={6} />
              <div style={{ display: "flex", flexWrap: "wrap", columnGap: 12, rowGap: 2, marginTop: 8 }}>
                {items.slice(0, 3).map((item) => (
                  <div
                    key={item.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 10,
                      color: SAGE[500],
                      fontFamily: F,
                    }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: 9999, background: item.color }} />
                    <span>
                      {item.name} {item.pct}%
                    </span>
                  </div>
                ))}
                {items.length > 3 && (
                  <span style={{ fontSize: 10, color: SAGE[400], fontFamily: F }}>
                    +{items.length - 3} more
                  </span>
                )}
              </div>
            </>
          ) : (
            // PHASE 4/5 — Salary data; rendered as empty-state cream tile for now.
            <div
              style={{
                marginTop: 4,
                padding: "10px 12px",
                borderRadius: 10,
                background: CREAM[200],
                border: `1px dashed ${SAGE[200]}`,
                color: SAGE[500],
                fontFamily: F,
                fontSize: 11,
                lineHeight: 1.4,
              }}
            >
              Salary data coming soon
            </div>
          )}
        </div>
        <div
          style={{
            padding: "10px 20px",
            borderTop: `1px solid ${t.border}`,
            background: t.bg,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.2,
                flex: 1,
                color: t.color,
                fontFamily: F,
              }}
            >
              {insight.headline}
            </span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: t.color }}>
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>
          <div style={{ fontSize: 10, marginTop: 2, lineHeight: 1.3, color: t.color, opacity: 0.75, fontFamily: F }}>
            {insight.detail}
          </div>
        </div>
      </div>
    );
  };

  const ROLES = slices.ROLES;
  const LOCATIONS = slices.LOCATIONS;
  const SENIORITY = slices.SENIORITY;
  const seniorStaffPct = slices.seniorStaffPct;

  return (
    <div className="fade-up" style={{ marginTop: 20, animationDelay: "0.1s" }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 12,
          paddingLeft: 4,
          paddingRight: 4,
        }}
      >
        <h2 style={{ fontSize: 18, fontWeight: 600, color: SAGE[800], fontFamily: SERIF, margin: 0 }}>
          Your fit at a glance
        </h2>
        <span style={{ fontSize: 12, color: SAGE[400], fontFamily: F }}>
          Across all {totalOpenRoles} open roles
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
        }}
      >
        <Tile
          label="Roles"
          headlineStat={ROLES.length > 0 ? `${ROLES[0].pct}%` : "—"}
          headlineLabel={ROLES.length > 0 ? ROLES[0].name : "no data"}
          items={ROLES}
          insight={insights.role}
        />
        <Tile
          label="Locations"
          headlineStat={LOCATIONS.length > 0 ? `${LOCATIONS[0].pct}%` : "—"}
          headlineLabel={LOCATIONS.length > 0 ? LOCATIONS[0].name : "no data"}
          items={LOCATIONS}
          insight={insights.location}
        />
        <Tile
          label="Seniority"
          headlineStat={`${seniorStaffPct}%`}
          headlineLabel="Senior–Staff"
          items={SENIORITY}
          insight={insights.seniority}
        />
        <Tile
          label="Comp"
          headlineStat="—"
          headlineLabel="vs market"
          insight={insights.comp}
          isComp
        />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Tab content — Hiring Trends (placeholder)
// PHASE 2 — replace this with the real chart + duration histogram once the
// monthly aggregation lambda lands.
// ----------------------------------------------------------------------------
function HiringTrendsTab() {
  return (
    <div
      className="fade-up"
      style={{
        marginTop: 20,
        borderRadius: 16,
        padding: 48,
        background: CREAM[50],
        border: `2px dashed ${SAGE[200]}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        minHeight: 280,
      }}
    >
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={SAGE[300]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="M7 14l4-4 4 4 5-5" />
      </svg>
      <div style={{ marginTop: 14, fontSize: 16, fontWeight: 600, color: SAGE[600], fontFamily: SERIF }}>
        Hiring Trends — coming soon
      </div>
      <p style={{ marginTop: 8, fontSize: 13, color: SAGE[500], fontFamily: F, maxWidth: 520, lineHeight: 1.5 }}>
        We have created/updated timestamps on every posting; this section will plot
        postings/month and posting-duration distribution once we wire up the
        aggregation. Tracked as Phase 2.
      </p>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Tab content — Explore Roles (existing jobs table, restyled)
// ----------------------------------------------------------------------------
const FAMILY_COLORS = {
  Engineering: SAGE[600],
  "Data Science": "#6366f1",
  Product: WARM.accent,
  Design: WARM.rose,
  Marketing: "#b8993e",
  Sales: "#0ea5e9",
  Operations: SAGE[400],
};

function ExploreRolesTab({ company, jobs, summary }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [familyFilter, setFamilyFilter] = useState(null);
  const [marketFilter, setMarketFilter] = useState(null);

  const families = Object.entries(summary.by_family || {}).sort((a, b) => b[1] - a[1]);

  let filtered = jobs;
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(
      (j) =>
        j.title.toLowerCase().includes(q) ||
        (j.location || "").toLowerCase().includes(q) ||
        (j.job_family || "").toLowerCase().includes(q)
    );
  }
  if (familyFilter) filtered = filtered.filter((j) => j.job_family === familyFilter);
  if (marketFilter) filtered = filtered.filter((j) => j.markets.includes(marketFilter));

  const cardStyle = {
    background: CREAM[50],
    border: `2px solid ${SAGE[200]}`,
    borderRadius: 16,
    overflow: "hidden",
  };

  const chipStyle = (active, color) => ({
    fontSize: 11,
    padding: "5px 10px",
    borderRadius: 9999,
    fontFamily: F,
    fontWeight: active ? 600 : 500,
    background: active ? color : CREAM[200],
    color: active ? "#fff" : SAGE[700],
    border: "none",
    cursor: "pointer",
    transition: "all 0.15s",
  });

  return (
    <div className="fade-up" style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Filters card */}
      {families.length > 0 && (
        <div style={{ ...cardStyle, padding: 16 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: SAGE[500], fontFamily: F, marginRight: 6 }}>
              Filter:
            </span>
            {families.map(([family, count]) => (
              <button
                key={family}
                onClick={() => setFamilyFilter(familyFilter === family ? null : family)}
                style={chipStyle(familyFilter === family, FAMILY_COLORS[family] || SAGE[600])}
              >
                {family} ({count})
              </button>
            ))}
            <span style={{ borderLeft: `1px solid ${SAGE[200]}`, height: 20, marginLeft: 8, marginRight: 8 }} />
            <button
              onClick={() => setMarketFilter(marketFilter === "seattle_msa" ? null : "seattle_msa")}
              style={chipStyle(marketFilter === "seattle_msa", SAGE[600])}
            >
              Seattle ({summary.seattle || 0})
            </button>
            <button
              onClick={() => setMarketFilter(marketFilter === "us_remote" ? null : "us_remote")}
              style={chipStyle(marketFilter === "us_remote", "#6366f1")}
            >
              Remote ({summary.remote || 0})
            </button>
          </div>

          <div style={{ marginTop: 12 }}>
            <input
              type="text"
              placeholder="Search jobs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: 300,
                maxWidth: "100%",
                padding: "8px 12px",
                fontSize: 13,
                fontFamily: F,
                background: CREAM[50],
                border: `1.5px solid ${SAGE[200]}`,
                borderRadius: 10,
                color: SAGE[800],
                outline: "none",
              }}
              onFocus={(e) => (e.target.style.borderColor = SAGE[400])}
              onBlur={(e) => (e.target.style.borderColor = SAGE[200])}
            />
          </div>
        </div>
      )}

      {/* Jobs table card */}
      {filtered.length > 0 ? (
        <div style={cardStyle}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${SAGE[200]}` }}>
                {["Title", "Family", "Level", "Location", "Market", ""].map((h, i) => (
                  <th
                    key={i}
                    style={{
                      textAlign: i === 5 ? "center" : "left",
                      padding: "12px 16px",
                      fontFamily: F,
                      fontSize: 11,
                      fontWeight: 600,
                      color: SAGE[600],
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      width: i === 5 ? 40 : "auto",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((job, idx) => (
                <tr
                  key={job.job_id}
                  style={{ borderBottom: idx === filtered.length - 1 ? "none" : `1px solid ${SAGE[100]}`, cursor: "pointer" }}
                  onClick={() =>
                    navigate(`/applications/preview/${job.company_domain || company.domain}/${job.job_id}`)
                  }
                  onMouseEnter={(e) => (e.currentTarget.style.background = SAGE[50])}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td
                    style={{
                      padding: "12px 16px",
                      fontFamily: F,
                      fontSize: 13,
                      color: SAGE[800],
                    }}
                  >
                    {job.title}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {job.job_family && (
                      <span
                        style={{
                          display: "inline-block",
                          padding: "3px 10px",
                          fontSize: 11,
                          fontFamily: F,
                          fontWeight: 500,
                          borderRadius: 9999,
                          background: FAMILY_COLORS[job.job_family]
                            ? `${FAMILY_COLORS[job.job_family]}18`
                            : SAGE[100],
                          color: FAMILY_COLORS[job.job_family] || SAGE[600],
                        }}
                      >
                        {job.job_family}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px", color: SAGE[600], fontFamily: F, fontSize: 13 }}>
                    {job.level || ""}
                  </td>
                  <td style={{ padding: "12px 16px", color: SAGE[600], fontFamily: F, fontSize: 12 }}>
                    {job.location}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      {job.markets.includes("seattle_msa") && (
                        <span
                          style={{
                            padding: "2px 8px",
                            fontSize: 10,
                            fontFamily: F,
                            fontWeight: 600,
                            borderRadius: 9999,
                            background: `${SAGE[600]}20`,
                            color: SAGE[700],
                          }}
                        >
                          SEA
                        </span>
                      )}
                      {job.markets.includes("us_remote") && (
                        <span
                          style={{
                            padding: "2px 8px",
                            fontSize: 10,
                            fontFamily: F,
                            fontWeight: 600,
                            borderRadius: 9999,
                            background: "#6366f118",
                            color: "#6366f1",
                          }}
                        >
                          Remote
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                    {job.url && (
                      <a
                        href={job.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View source posting"
                        style={{ color: SAGE[400], display: "inline-flex" }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            ...cardStyle,
            padding: 48,
            textAlign: "center",
            color: SAGE[500],
            fontFamily: F,
            fontSize: 14,
          }}
        >
          {jobs.length === 0
            ? "No jobs data yet — run the scraper to populate"
            : "No jobs match your filters"}
        </div>
      )}

      {filtered.length > 0 && (
        <span style={{ fontSize: 12, color: SAGE[400], fontFamily: F, paddingLeft: 4 }}>
          Showing {filtered.length} of {jobs.length} Seattle + Remote jobs
          {summary.total_all_markets > 0 && ` (${summary.total_all_markets} total across all markets)`}
        </span>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Generic placeholder card
// ----------------------------------------------------------------------------
function PlaceholderCard({ title, body }) {
  return (
    <div
      className="fade-up"
      style={{
        marginTop: 20,
        borderRadius: 16,
        padding: 48,
        background: CREAM[50],
        border: `2px dashed ${SAGE[200]}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        minHeight: 240,
      }}
    >
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={SAGE[300]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 3" />
      </svg>
      <div style={{ marginTop: 14, fontSize: 16, fontWeight: 600, color: SAGE[600], fontFamily: SERIF }}>
        {title}
      </div>
      <p style={{ marginTop: 8, fontSize: 13, color: SAGE[500], fontFamily: F, maxWidth: 520, lineHeight: 1.5 }}>
        {body}
      </p>
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================
const CompanyInsightsPage = () => {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const tabFromUrl = searchParams.get("tab");
  const activeTab = TABS.find((t) => t.id === tabFromUrl && !t.soon)?.id || "hiring";

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await fetch(`${API_URL}/api/companies/${domain}`, {
          headers: { ...mockAuthHeaders() },
          credentials: "include",
        });
        if (!res.ok) throw new Error(`${res.status}`);
        setCompany(await res.json());
      } catch (err) {
        console.error("Failed to load company:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, [domain]);

  const setActiveTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "hiring") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const slices = useMemo(() => {
    if (!company || !company.jobs) return { ROLES: [], LOCATIONS: [], SENIORITY: [], seniorStaffPct: 0 };
    return computeFitSlices(company.jobs);
  }, [company]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <div style={{ width: 32, height: 32, border: `3px solid ${SAGE[200]}`, borderTop: `3px solid ${SAGE[500]}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!company) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <span style={{ color: SAGE[500], fontFamily: F }}>Company not found: {domain}</span>
      </div>
    );
  }

  const summary = company.jobs_summary || {};
  const totalOpenRoles = summary.total_all_markets || summary.total || 0;

  return (
    <div style={{ fontFamily: F, color: SAGE[900] }}>
      <link href="https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Outfit:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.4s ease-out both; }
        @keyframes fadeDown { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .fade-down { animation: fadeDown 0.25s ease-out both; }
        .values-grid { grid-template-columns: 1fr 1fr; }
        @media (max-width: 900px) { .values-grid { grid-template-columns: 1fr; } }
      `}</style>

      <div style={{ maxWidth: 1152, margin: "0 auto", padding: "8px 8px 32px" }}>
        {/* Breadcrumb */}
        <div className="fade-up" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
          <button
            onClick={() => navigate("/explore/companies")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 500,
              color: SAGE[500],
              fontFamily: F,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Companies
          </button>
          <span style={{ color: SAGE[300] }}>/</span>
          <span style={{ fontSize: 12, fontWeight: 500, color: SAGE[700], fontFamily: F }}>{company.name}</span>
          <span style={{ color: SAGE[300] }}>/</span>
          <span style={{ fontSize: 12, fontWeight: 500, color: SAGE[800], fontFamily: F }}>
            {TABS.find((t) => t.id === activeTab)?.label}
          </span>
        </div>

        <CompanyHeader company={company} totalOpenRoles={totalOpenRoles} />
        <FitStrip slices={slices} totalOpenRoles={totalOpenRoles} />

        <TabBar activeTab={activeTab} onChange={setActiveTab} />

        {activeTab === "hiring" && <HiringTrendsTab />}
        {activeTab === "explore" && (
          <ExploreRolesTab company={company} jobs={company.jobs || []} summary={summary} />
        )}
        {/* PHASE 3 — Company Values: stated + revealed (JD phrase frequencies). */}
        {activeTab === "values" && (
          <PlaceholderCard
            title="Company Values — coming soon"
            body="Stated values + JD phrase frequencies are coming soon. Depends on the JD analysis pipeline currently being built elsewhere (Phase 3)."
          />
        )}
        {/* PHASE 3 — Employee Benefits extracted from JDs. */}
        {activeTab === "benefits" && (
          <PlaceholderCard
            title="Employee Benefits — coming soon"
            body="Benefits extracted from JDs — coming with Phase 3 JD analysis."
          />
        )}
        {/* News tab is disabled in TabBar; reaching this branch shouldn't happen. */}
      </div>
    </div>
  );
};

export default CompanyInsightsPage;
